/**
 * Tiny subscriber store that carries the image view's zoom/pan published by
 * `ProgressiveImageWeb`. `AnnotationLayer` and `AnnotationsOverlay` subscribe so the
 * SVG overlay can mirror the image's CSS transform. Keyed by documentId so switching
 * images doesn't leak state across images.
 *
 * The store also holds an imperative `ImageViewController` registry so the annotation
 * controller can drive native Zoomable through one shared bridge.
 */
import type { ImageViewController } from '@v2/components/imageHandling/components/DrawingCanvas/types/imageViewController';

export interface ImageViewTransform {
  zoom: number;
  panX: number;
  panY: number;
}

const DEFAULT: ImageViewTransform = { zoom: 1, panX: 0, panY: 0 };

const state = new Map<number, ImageViewTransform>();
const listeners = new Set<() => void>();

export const getImageViewTransform = (documentId: number | null | undefined): ImageViewTransform => {
  if (documentId == null) return DEFAULT;
  return state.get(documentId) ?? DEFAULT;
};

export const setImageViewTransform = (documentId: number, next: ImageViewTransform): void => {
  const current = state.get(documentId);
  if (current && current.zoom === next.zoom && current.panX === next.panX && current.panY === next.panY) {
    return;
  }
  state.set(documentId, next);
  listeners.forEach((l) => l());
};

export const subscribeImageViewTransform = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const controllers = new Map<number, ImageViewController>();

export const registerImageViewController = (documentId: number, controller: ImageViewController): (() => void) => {
  controllers.set(documentId, controller);
  return () => {
    if (controllers.get(documentId) === controller) controllers.delete(documentId);
  };
};

export const getImageViewController = (documentId: number | null | undefined): ImageViewController | null => {
  if (documentId == null) return null;
  return controllers.get(documentId) ?? null;
};
